import React from 'react';
import { Text } from 'ilenia';
import { StyleSheet, css } from 'aphrodite';
import { ArrowDown, Cross } from '../../Assets/images/svg';

interface MultiSelectProps {
    value: string[];
    onChange: any;
    options: Array<{value: string, label: string}>;
    disabled?: boolean;
}

interface MultiSelectState {
    isOpen: boolean;
}

class MultiSelect extends React.Component<MultiSelectProps, MultiSelectState> {
    constructor(props: MultiSelectProps) {
        super(props);

        this.state = {
            isOpen: false,
        };

        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onOptionSelect = this.onOptionSelect.bind(this);
        this.onOptionUnselect = this.onOptionUnselect.bind(this);
    }

    public onOpen() {
        if (!this.state.isOpen && !this.props.disabled) {
            this.setState({isOpen: true});
        }
    }

    public onClose(event: any) {
        const component = this as any;
        const currentTarget = event.currentTarget;
        setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
                component.setState({isOpen: false});
            }
        }, 0);
    }

    public onOptionSelect(event: any) {
        const {value} = this.props;
        const selected = event.target.dataset.value;
        if (value.includes(selected)) {
            this.onOptionUnselect(selected)();
        } else {
            value.push(selected);
            this.props.onChange(value);
        }
    }

    public onOptionUnselect(option: any) {
        return () => {
            const {value} = this.props;
            const index = value.indexOf(option);
            if (index > -1) {
                value.splice(index, 1);
            }
            this.props.onChange(value);
        };
    }

    public render() {
        const {isOpen} = this.state;
        const {value = [], options} = this.props;
        const selectedOptions = value.slice(0, 2).map((s: any) => {
            const option = this.props.options.find((o: any) => o.value === s);
            return option ? (
                <div className={css(styles.selectedOption)}>
                    <div className={css(styles.tag)}>{option.label}</div>
                    <div className={css(styles.cross)} onClick={this.onOptionUnselect(s)}>
                        <Cross className={css(styles.trustpilotIcon)}/>
                    </div>
                </div>
            ) : null;
        });
        return (
            <div className={css(styles.wrapper, styles.wrapperPseudo)} onClick={this.onOpen} onBlur={this.onClose} tabIndex={0}>
                <div className={css(styles.input, styles.inputPseudo, isOpen && styles.inputActive)}>
                    <div>
                        <div className={css(styles.selectedOptions)}>{selectedOptions}</div>
                        {value.length > 2 ? (
                            <div className={css(styles.selectedOption, styles.selectedOptionDark)}>
                                <div className={css(styles.tag)}>+{value.length - 2}</div>
                            </div>
                        ) : null}
                    </div>
                    <ArrowDown className={css(styles.inputArrow, styles.inputArrowPseudo)}/>
                </div>
                <div className={css(styles.options, isOpen && styles.optionsOpen)}>
                    {options && options.length > 0 ? (
                        options.map((option: any) => (
                            <div
                                className={css(
                                    styles.option,
                                    styles.optionPseudo,
                                    value.includes(option.value) && styles.optionSelected,
                                )}
                                data-value={option.value}
                                onClick={this.onOptionSelect}
                                key={option.value}
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className={css(styles.noOptions)}>
                            <Text id="multiSelect.noOptions"/>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    wrapper: {
        boxSizing: 'border-box',
        width: '350px',
    },
    wrapperPseudo: {
        '&:focus': {
            outline: 'none',
        },
    },
    input: {
        background: 'rgb(255, 255, 255)',
        borderRadius: '4px',
        minHeight: '40px',
        width: '100%',
        boxShadow: 'inset 0px 1px 3px 0px rgba(0, 0, 0, 0.13)',
        border: '1px solid rgb(212, 212, 212)',
        cursor: 'text',
        position: 'relative',
        padding: '6px 20px 2px 11px',
        display: 'inline-block',
        whiteSpace: 'normal',
        overflowX: 'hidden',
        lineHeight: '18px',
        boxSizing: 'border-box',
    },
    inputPseudo: {
        '&:hover': {
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)',
            background: 'rgb(255, 255, 255)',
            boxSizing: 'border-box',
        }
    },
    inputActive: {
        boxShadow: '0 0 5px 0 rgb(162, 211, 255)',
        border: '1px solid rgb(162, 211, 255)',
        boxSizing: 'border-box',
    },
    inputArrow: {
        position: 'absolute',
        top: '19px',
        right: '16px',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        height: '8px',
        width: '8px',
        boxSizing: 'border-box',
    },
    inputArrowPseudo: {
        '>path': {
            fill: 'rgb(126, 126, 126)',
            boxSizing: 'border-box',
        },
    },
    options: {
        display: 'none',
        background: 'rgb(255, 255, 255)',
        borderRadius: '3px',
        boxShadow: '0px 0px 0px 1px rgb(223, 226, 229), 0px 2px 16px 0px rgba(33, 43, 54, 0.08)',
        minHeight: '44px',
        maxHeight: '172px',
        width: '350px',
        marginTop: '5px',
        padding: '6px 0',
        overflowY: 'scroll',
        position: 'absolute',
        zIndex: 1,
        boxSizing: 'border-box',
    },
    optionsOpen: {
        display: 'block',
        boxSizing: 'border-box',
    },
    option: {
        // Frame
        background: 'rgb(255, 255, 255)',
        borderRadius: '0px',
        height: '32px',
        width: '100%',
        padding: '6px 0 6px 11px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        // Text
        color: 'rgb(41, 41, 41)',
        fontFamily: 'Lato',
        fontSize: '14px',
        fontWeight: 'normal',
        letterSpacing: '0px',
        lineHeight: '20px',
        boxSizing: 'border-box',
    },
    optionPseudo: {
        '&:hover': {
            background: 'rgba(54, 140, 215, 0.07)',
            boxSizing: 'border-box',
        },
    },
    optionSelected: {
        backgroundColor: 'rgb(136, 136, 136)',
        color: 'rgb(255, 255, 255)',
    },
    noOptions: {
        color: 'rgba(0,0,0,.4)',
        fontFamily: 'Lato',
        fontSize: '14px',
        fontWeight: 'normal',
        letterSpacing: '0px',
        lineHeight: '20px',
        padding: '6px 0 6px 11px',
        boxSizing: 'border-box',
    },
    selectedOptions: {
        maxWidth: '270px',
        overflow: 'hidden',
        display: 'inline-flex',
        justifyContent: 'flex-end',
        verticalAlign: 'bottom',
    },
    selectedOption: {
        background: 'rgb(255, 255, 255)',
        borderRadius: '2.56px',
        border: '1px solid rgb(223, 226, 229)',
        padding: '2px 2px 2px 8px',
        width: 'auto',
        display: 'inline-block',
        cursor: 'default',
        margin: '0 4px 4px 0',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
    },
    selectedOptionDark: {
        background: 'rgb(136, 136, 136)',
        borderRadius: '2.56px',
        padding: '2px 6px',
        color: 'rgb(255, 255, 255)',
    },
    tag: {
        display: 'inline-block',
        boxSizing: 'border-box',
    },
    cross: {
        display: 'inline-block',
        marginLeft: '6px',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    trustpilotIcon: {
        height: '9px',
        width: '9px',
        verticalAlign: 'initial',
        boxSizing: 'border-box',
    }
});

export default MultiSelect;



// WEBPACK FOOTER //
// ./src/CommonUI/Control/Multiselect.tsx